import { IconButton, Tooltip } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { FunctionComponent, ReactElement, useState } from "react";
import { FaFileExport } from "react-icons/fa";

// Components
import CustomModal from "../../../shared/Modals/CustomModal";

// Hooks
import { useAuth } from "../../../hooks/useAuth";
import useClaimsToast from "../../../hooks/useToast";
import usePermission from "../../../hooks/usePermission";

// Protos
import { ExportEntityType } from "../../../utils/api/protos/messages/claims.message_pb";

// Utils
import { exportTableToCsv } from "../../../utils/csvExport";
import { getCacheKeyObject } from "../../../shared/DataTable/hooks/useSearchParams";
import { UserRole } from "../../../utils/consts";

interface ExportClaimsButtonProps {
	cacheKey: string; // The actual cacheKey from the DataTable
	assignedAnalystId?: number;
	selectedClaimIds?: Array<number>; // Selected claim IDs take priority over filters
	onExport?: () => void;
}

/**
 * Button component to export claims based on current filters/search
 * @param props typeof: ExportClaimsButtonProps
 * @returns ReactElement
 */
const ExportClaimsButton: FunctionComponent<ExportClaimsButtonProps> = (
	props: ExportClaimsButtonProps
): ReactElement => {
	const { cacheKey, assignedAnalystId, selectedClaimIds, onExport } = props;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
	const { getUser } = useAuth();
	const { hasGeneralClaimsAccess } = usePermission();
	const user = getUser();
	const toast = useClaimsToast();

	// Roles allowed to export claims
	const allowedRoles: Array<UserRole> = [
		UserRole.ClaimsAdmin,
		UserRole.ClaimsManager,
		UserRole.ClaimsAnalyst,
		UserRole.ClaimsNetworkManager,
		UserRole.ClaimsViewOnly,
		UserRole.Payments
	];

	const handleExportClaims = async (): Promise<void> => {
		setIsLoading(true);

		// Use the exact cacheKey that was used for the table data fetch
		// This ensures we export the same data that's displayed
		console.log("Export using cacheKey:", cacheKey);

		const currentParams = getCacheKeyObject(cacheKey);
		console.log("Parsed params object:", currentParams);
		console.log("SearchField:", currentParams.searchField);
		console.log("SearchString:", currentParams.searchString);

		try {
			// Build filters object from current params
			const filters: Record<string, string | number> = {};
			const hasSelectedClaims = selectedClaimIds && selectedClaimIds.length > 0;

			// Only add filters if no specific claims are selected
			if (!hasSelectedClaims) {
				// Add search filters if present
				if (currentParams.searchField && currentParams.searchString) {
					filters.search_field = currentParams.searchField;
					filters.search_string = currentParams.searchString;
				}

				// Add assigned analyst filter if present
				if (assignedAnalystId) {
					filters.assigned_analyst_id = assignedAnalystId;
				}
			}

			console.log("Selected claim IDs:", selectedClaimIds);
			console.log("Filters:", filters);
			console.log("Sort:", {
				sortField: currentParams.sortField,
				sortDir: currentParams.sortDirection
			});

			// Call the dynamic export function
			const result = await exportTableToCsv({
				entityType: ExportEntityType.EXPORT_ENTITY_TYPE_CLAIMS,
				filters: hasSelectedClaims ? undefined : filters,
				ids: hasSelectedClaims ? selectedClaimIds : undefined,
				sortField: currentParams.sortField,
				sortDir: currentParams.sortDirection as "asc" | "desc" | undefined
			});

			console.log("Export Success:", result);
			toast("Success", `Successfully exported ${result.totalRecords} claims`, "success");
		} catch (err) {
			const error = err as RpcError;
			console.error("Export Error:", error);
			console.error("Error code:", error.code);
			console.error("Error message:", error.message);

			// Handle specific error codes
			if (error.code === 7) {
				// PERMISSION_DENIED
				toast("Permission Denied", "You don't have permission to export claims", "error");
			} else if (error.code === 16) {
				// UNAUTHENTICATED
				toast("Session Expired", "Your session has expired. Please refresh and try again.", "error");
			} else {
				toast("Error", error.message || "Failed to export claims", "error");
			}
		} finally {
			setIsLoading(false);
			if (isExportModalOpen) setIsExportModalOpen(false);
			if (onExport) onExport();
		}
	};

	const canUserExport = (): void => {
		if (user?.role.split(",").some((role: string) => allowedRoles.includes(role as UserRole))) {
			setIsExportModalOpen(true);
		} else {
			toast("Access Denied", "You do not have the correct permission to export claims", "warning");
		}
	};

	const getExportModal = (): ReactElement => {
		const hasSelectedClaims = selectedClaimIds && selectedClaimIds.length > 0;
		const exportMessage = hasSelectedClaims
			? `Export ${selectedClaimIds.length} selected claim${selectedClaimIds.length === 1 ? "" : "s"}?`
			: `Export claims based on current filters and search criteria?`;

		return (
			<CustomModal
				isOpen={isExportModalOpen}
				title={"Export Claims"}
				size="lg"
				message={exportMessage}
				onContinue={() => handleExportClaims()}
				onCancel={() => setIsExportModalOpen(false)}
				continueText={"Export"}
				continueLoading={isLoading}
			/>
		);
	};

	return (
		<>
			{isExportModalOpen && getExportModal()}
			<Tooltip label="Export Claims to CSV">
				<IconButton
					aria-label="Export claims"
					icon={<FaFileExport />}
					size="sm"
					variant="solid"
					colorScheme="orange"
					onClick={() => canUserExport()}
					isDisabled={!hasGeneralClaimsAccess}
				/>
			</Tooltip>
		</>
	);
};

export default ExportClaimsButton;
